import type { JSX } from "react";
import type { ReviewStepEvent } from "../../../data/chatQuery";

// [S-509e] Map a clause's review state to a progress-pill intent. The current
// clause (the `currentIndex` segment) always reads purple regardless of its
// recorded decision; otherwise the decision drives the color. Returns the state
// key used for both the CSS class (`is-<state>`) and the `data-state` attr so the
// pills are assertable in tests and colored via @csai/design-system tokens.
export function stepState(
  decision: ReviewStepEvent["steps"][number]["decision"],
  isCurrent: boolean
): "cur" | "applied" | "kept" | "skipped" | "pending" {
  if (isCurrent) return "cur";
  if (decision === "applied") return "applied";
  if (decision === "kept") return "kept";
  if (decision === "skipped") return "skipped";
  return "pending";
}

// [S-509h] The review PLAN header: eyebrow + "Clause M of N" + a color-coded
// segmented progress bar (one pill per clause) + legend. Driven entirely by the
// `steps` array — it never assumes how many clauses there are, their order, or
// their priority; everything comes from the data the agent supplied. `total` is
// passed explicitly (rather than `steps.length`) to match the backend's count,
// which is the authority even mid-stream.
export function ReviewPlan({
  steps,
  currentIndex,
  total,
}: {
  steps: ReviewStepEvent["steps"];
  currentIndex: number;
  total: number;
}): JSX.Element {
  return (
    <div className="sa-rev__head">
      <div className="sa-rev__eyebrow">
        <span className="sa-rev__eyebrow-dot" aria-hidden="true" />
        Multi-step review
      </div>
      <div className="sa-rev__title">Clause-by-clause</div>
      <div className="sa-rev__count">
        {/* [S-800] `currentIndex` is a position over EVERY clause; `total`
            counts only the walked ones — so M counts walked clauses up to it. */}
        <b>Review {Math.min(Math.max(steps.filter((s) => s.needsReview && s.index <= currentIndex).length, 1), total)}</b> of {total}
      </div>
      {(() => {
        // [S-509h/M1] Triage lens: lead with how much actually needs attention.
        // Flagged = anything not low-risk (a deviation from the certified
        // standard); high-risk is called out separately.
        const flagged = steps.filter((s) => s.risk !== "low").length;
        const highRisk = steps.filter((s) => s.risk === "high").length;
        if (flagged === 0) return null;
        return (
          <div className="sa-rev__triage">
            {flagged} of {total} flagged
            {highRisk > 0 ? (
              <>
                {" · "}
                <b className="sa-rev__triage-high">{highRisk} high-risk</b>
              </>
            ) : null}
          </div>
        );
      })()}
      <div className="sa-rev__steps">
        {steps.map((s) => {
          const state = stepState(s.decision, s.index === currentIndex);
          return (
            <i
              key={s.index}
              className={`sa-rev__step is-${state}`}
              data-state={state}
            />
          );
        })}
      </div>
      <div className="sa-rev__legend" aria-hidden="true">
        <span className="sa-rev__legend-item">
          <i className="sa-rev__legend-dot is-applied" /> applied
        </span>
        <span className="sa-rev__legend-item">
          <i className="sa-rev__legend-dot is-skipped" /> skipped
        </span>
        <span className="sa-rev__legend-item">
          <i className="sa-rev__legend-dot is-kept" /> kept
        </span>
        <span className="sa-rev__legend-item">
          <i className="sa-rev__legend-dot is-cur" /> current
        </span>
      </div>
    </div>
  );
}
